/**
 * The proposals and the overrides, read once for the whole Admin page.
 *
 * Two cards used to load these independently — the "passed, not yet built" card and the League
 * configuration table both read the docket, and the Grid editor and Set by hand both read the
 * override log. One subscription serves all of them, and the cards cannot disagree about which
 * rules have passed because they are looking at the same array.
 */

import { useEffect, useMemo, useState } from 'react';
import { notYetActive, type PassedRule } from '../domain/effects.js';
import type { Override } from '../domain/overrides.js';
import type { LeagueStore, RuleProposal } from '../store/types.js';

const SEASON = 2026;

export function useLeagueRules(store: LeagueStore) {
  const [proposals, setProposals] = useState<RuleProposal[]>([]);
  const [overrides, setOverrides] = useState<Override[]>([]);

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([store.proposals(SEASON), store.overrides(SEASON)])
      .then(([ps, os]) => { if (live) { setProposals(ps); setOverrides(os); } })
      .catch(() => {});
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'proposals' || what === 'overrides') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  /** Everything the committee has already decided, in docket order. */
  const passed: PassedRule[] = useMemo(() => proposals
    .filter((p) => p.status === 'passed' || p.status === 'provisional')
    .map((p) => ({
      id: p.id,
      title: p.title,
      status: p.status as 'passed' | 'provisional',
      effect: p.effect,
      docketRef: `${p.season} docket`,
      tally: p.votes
        ? {
          yes: Object.values(p.votes).filter((v) => v === 'yes').length,
          no: Object.values(p.votes).filter((v) => v === 'no').length,
        }
        : undefined,
    })), [proposals]);

  /** Passed rules the app does not enforce yet — the card that says the app is wrong. */
  const stuck = useMemo(() => notYetActive(passed), [passed]);

  return { proposals, overrides, passed, stuck };
}
